import { inferProductFromHost } from '../config/domainMap.js';

/**
 * Classe para manipulação de itens de sessão 
 * 
 * @author Wellington Pereira
 * @since 2014-03-11
 * @version 6.0.0
 */
class Session {
    #resolveScopedConfig(key) {
      const normalizedKey = String(key || '').trim();
      if (!normalizedKey) {
        return {
          scopedKey: normalizedKey,
          fallbackToLegacy: false,
        };
      }

      if (normalizedKey === 'token') {
        const hostname = typeof window !== 'undefined'
          ? window.location?.hostname || ''
          : '';
        const product = inferProductFromHost(hostname) || 'platform';
        return {
          scopedKey: `token:${product}`,
          fallbackToLegacy: true,
        };
      }

      if (normalizedKey === 'theme') {
        const hostname = typeof window !== 'undefined'
          ? window.location?.hostname || ''
          : '';
        const normalizedHost = String(hostname || '').trim().toLowerCase();
        return {
          scopedKey: normalizedHost ? `theme:${normalizedHost}` : normalizedKey,
          fallbackToLegacy: false,
        };
      }

      return {
        scopedKey: normalizedKey,
        fallbackToLegacy: false,
      };
    }

    /**
     * Limpa todos os itens de sessão.
     *
     * @returns {null}
     */
    clear() {
      sessionStorage.clear();
      return null;
    }

    /**
     * Exclui um item de sessão
     * 
     * @param {string} key 
     * @returns {null};
     */
    delete(key) {
      const { scopedKey } = this.#resolveScopedConfig(key);
      sessionStorage.removeItem(scopedKey);
      if (scopedKey !== key) {
        sessionStorage.removeItem(key);
      }
      return null;
    }
  
    /**
     * Recupera um item de sessão
     * 
     * @param {string} key
     * @returns {mixed} Retorna op valor armazenado 
     */
    get(key) {
      const { scopedKey, fallbackToLegacy } = this.#resolveScopedConfig(key);
      let item = sessionStorage.getItem(scopedKey);
      if (item === null && fallbackToLegacy && scopedKey !== key) {
        item = sessionStorage.getItem(key);
      }
      if (/^\{.+\}$/.test(item) || /^\[.+\]$/.test(item)) {
        item = JSON.parse(item);
      }
      return item;
    }
  
    /**
     * Define um item de sessão
     * 
     * @param {string} key 
     * @param {mixed} value 
     * @returns {mixed} Retorna o valor passado 
     */
    set(key, value) {
      const { scopedKey } = this.#resolveScopedConfig(key);
      if (value === null || value === undefined) {
        sessionStorage.removeItem(scopedKey);
        if (scopedKey !== key) {
          sessionStorage.removeItem(key);
        }
        return value;
      }

      let newValue = null;
      if (typeof value === 'object') {
        newValue = JSON.stringify(value);
      } else {
        newValue = value;
      }
  
      sessionStorage.setItem(scopedKey, newValue);
      if (scopedKey !== key) {
        sessionStorage.removeItem(key);
      }
      return value;
    }
  }

  const session = new Session();
  export default session;
